import type {
  DataSeries,
  MeasurementPoint,
  MeasurementValue,
} from "./generated/models.ts";

export type CurrentMeasurement = Pick<
  MeasurementValue,
  "id" | "intervalStart" | "intervalEnd" | "value" | "quality" | "revision"
>;

function legacyIntervalSeconds(series: DataSeries): number {
  // Only fixed legacy steps are unambiguous. A calendar day/month needs
  // explicit bounds (DST and month length must never be guessed here).
  const seconds =
    series.intervalSeconds ??
    (series.granularity === "hour"
      ? 3600
      : series.granularity === "ten_minutes"
        ? 600
        : undefined);
  if (
    typeof seconds !== "number" ||
    !Number.isFinite(seconds) ||
    seconds <= 0
  ) {
    throw new Error(
      `Migration de la série ${series.id} impossible : intervalSeconds positif requis pour la granularité ${series.granularity} ; aucune durée n’a été déduite.`,
    );
  }
  return seconds;
}

function legacyValue(
  series: DataSeries,
  point: MeasurementPoint,
  index: number,
): MeasurementValue {
  const start = new Date(point.timestamp);
  const end = new Date(start.getTime() + legacyIntervalSeconds(series) * 1000);
  return {
    id: `${series.id}:legacy:${String(index)}`,
    intervalStart: start.toISOString(),
    intervalEnd: end.toISOString(),
    value: point.value,
    quality: point.value === null ? "missing" : "measured",
    revision: 1,
  };
}

/** Uses the last valid revision of each interval without losing older values. */
export function currentMeasurements(series: DataSeries): CurrentMeasurement[] {
  const values =
    series.values?.length || series.points.length === 0
      ? (series.values ?? [])
      : series.points.map((point, index) => legacyValue(series, point, index));
  const latest = new Map<string, MeasurementValue>();
  for (const value of values) {
    const key = value.intervalStart;
    const previous = latest.get(key);
    if (!previous || value.revision > previous.revision) latest.set(key, value);
  }
  return [...latest.values()]
    .sort((left, right) =>
      left.intervalStart.localeCompare(right.intervalStart),
    )
    .map((value) => value);
}

/** Adds identity and revision data without changing legacy points or existing revisions. */
export function migrateSeriesMeasurementRevisions(
  series: DataSeries,
): DataSeries {
  if (series.values?.length || series.points.length === 0) return series;
  return {
    ...series,
    intervalSeconds: legacyIntervalSeconds(series),
    values: series.points.map((point, index) =>
      legacyValue(series, point, index),
    ),
  };
}

export function validateMeasurementRevisions(series: DataSeries): boolean {
  if (!series.values) return true;
  const ids = new Set<string>();
  const valuesById = new Map<string, MeasurementValue>();
  const byInterval = new Map<string, Set<number>>();
  for (const value of series.values) {
    const start = Date.parse(value.intervalStart);
    const end = Date.parse(value.intervalEnd);
    if (
      !value.id ||
      ids.has(value.id) ||
      !Number.isInteger(value.revision) ||
      value.revision < 1 ||
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      end <= start ||
      (value.quality === "missing"
        ? value.value !== null
        : typeof value.value !== "number" || !Number.isFinite(value.value))
    )
      return false;
    ids.add(value.id);
    valuesById.set(value.id, value);
    const revisions = byInterval.get(value.intervalStart) ?? new Set<number>();
    if (revisions.has(value.revision)) return false;
    revisions.add(value.revision);
    byInterval.set(value.intervalStart, revisions);
  }
  for (const value of series.values) {
    if (value.revision === 1) {
      if (value.supersedesValueId) return false;
      continue;
    }
    const superseded = value.supersedesValueId
      ? valuesById.get(value.supersedesValueId)
      : undefined;
    if (
      !superseded ||
      superseded.intervalStart !== value.intervalStart ||
      superseded.intervalEnd !== value.intervalEnd ||
      superseded.revision !== value.revision - 1
    )
      return false;
  }
  return true;
}
